import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetModerationCasesQuery } from "@/redux/service";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import type {
  ModerationCase,
  ModerationCaseQueryParams,
} from "@/redux/moderation/types";
import type { PaginatedResponse } from "@/redux/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { Roles } from "@/shared/utils/constants";
import type { ModerationQueue } from "@/domains/moderation/types";
import { MODERATION_STATES } from "@/domains/moderation/constants";

const QUEUE_QUERY: ModerationCaseQueryParams = {
  state: MODERATION_STATES.PENDING_HUMAN,
  ordering: "created_at",
  pageSize: 1,
};

const summarise = (
  data: ModerationCase[] | PaginatedResponse<ModerationCase> | undefined,
) => {
  if (!data) {
    return { count: 0, oldest: null };
  }
  const rows = isPaginatedResponse(data) ? data.results : data;
  return {
    count: isPaginatedResponse(data) ? data.count : rows.length,
    oldest: rows[0]?.createdAt ?? null,
  };
};

export const useModerationQueue = (): ModerationQueue => {
  const user = useSelector(selectCurrentUser);
  const { pathname } = useLocation();
  const isAdmin = user?.role === Roles.ADMIN.value;

  const posts = useGetModerationCasesQuery(
    isAdmin ? { ...QUEUE_QUERY, contentKind: "discussion" } : skipToken,
  );
  const comments = useGetModerationCasesQuery(
    isAdmin ? { ...QUEUE_QUERY, contentKind: "comment" } : skipToken,
  );
  const { refetch: refetchPosts } = posts;
  const { refetch: refetchComments } = comments;

  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (!isAdmin || lastPathname.current === pathname) {
      return;
    }
    lastPathname.current = pathname;
    refetchPosts();
    refetchComments();
  }, [isAdmin, pathname, refetchPosts, refetchComments]);

  const postSummary = summarise(posts.data);
  const commentSummary = summarise(comments.data);
  const oldest = [postSummary.oldest, commentSummary.oldest]
    .filter((date): date is string => date !== null)
    .sort()[0];

  return {
    isAdmin,
    count: postSummary.count + commentSummary.count,
    postCount: postSummary.count,
    commentCount: commentSummary.count,
    oldestCreatedAt: oldest ?? null,
  };
};
